// Shared helper to derive the canonical Sentry `tags.route` value.
// Used by both SSR (Cloudflare Worker) and the browser so events line up in
// Sentry alerts and queries no matter where they originate.
//
// Rules (apply in this order):
//   1. Accept either a full URL or a bare pathname.
//   2. Drop the query string and hash — route tags must not carry user input
//      or cardinality-blowing params.
//   3. Lowercase the pathname (routes are case-insensitive for tagging).
//   4. Collapse repeated `/` and strip the trailing slash, except for "/".
//   5. Mask high-cardinality segments so dashboards group correctly:
//        - UUIDs                                       -> :id
//        - numeric ids                                 -> :id
//        - long opaque tokens (>=24 chars, no slashes) -> :token
//   6. Hard-cap to 200 chars to keep the tag indexable.
//
// If the input is unusable, return "/unknown" so events still group somewhere
// instead of silently dropping the tag.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;
const OPAQUE_RE = /^[A-Za-z0-9_-]{24,}$/;

function maskSegment(seg: string): string {
  if (UUID_RE.test(seg)) return ":id";
  if (NUMERIC_RE.test(seg)) return ":id";
  if (OPAQUE_RE.test(seg)) return ":token";
  return seg;
}

function extractPathname(input: string): string | undefined {
  if (!input) return undefined;
  if (input.startsWith("/")) {
    const q = input.indexOf("?");
    const h = input.indexOf("#");
    const cut = [q, h].filter((i) => i >= 0).sort((a, b) => a - b)[0];
    return cut === undefined ? input : input.slice(0, cut);
  }
  try {
    return new URL(input).pathname;
  } catch {
    return undefined;
  }
}

export function deriveRouteTag(input: string | URL | undefined | null): string {
  if (input == null) return "/unknown";
  const raw =
    typeof input === "string" ? extractPathname(input) : input.pathname;
  if (!raw) return "/unknown";

  let path = raw.replace(/\/{2,}/g, "/").toLowerCase();
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  if (path === "") path = "/";

  const masked =
    "/" +
    path
      .split("/")
      .filter(Boolean)
      .map(maskSegment)
      .join("/");

  const out = masked.length > 1 && masked.endsWith("/") ? masked.slice(0, -1) : masked;
  return out.length > 200 ? out.slice(0, 200) : out;
}